"use client";

import { useId, useState, type ReactNode } from "react";
import { useTranslations } from "use-intl";

import { NotificationIcon } from "@/components/appIcons";
import { BestDealOfferEditionRow } from "@/components/marketplace/best-deal-offer-edition-row";
import { DealOfferCheckedAt } from "@/components/marketplace/deal-offer-checked-at";
import { DealOfferHistoryButton } from "@/components/marketplace/deal-offer-history-button";
import { DealOfferPanel } from "@/components/marketplace/deal-offer-panel";
import { useDealOfferPriceAlert } from "@/components/marketplace/deal-offer-price-alert-context";
import { OfferPriceHistory } from "@/components/marketplace/deal-offer-price-history-modal";
import type { DealOffer } from "@/components/marketplace/deal-offers-types";
import { effectiveDealPrice } from "@/components/marketplace/deal-offers-utils";
import { Price } from "@/components/Price";
import { Button } from "@/components/ui/button";

function offerFormatLabel(
  offer: DealOffer,
  tFormats: ReturnType<typeof useTranslations<"shared.releaseFilters">>,
) {
  if (offer.formatCategory && tFormats.has(offer.formatCategory)) {
    return tFormats(offer.formatCategory);
  }
  return offer.formatCategory ?? tFormats("other");
}

function LastAvailablePrice({ offer }: { offer: DealOffer }) {
  const t = useTranslations("shared.affiliates");
  const amount = offer.currentPrice ?? effectiveDealPrice(offer);
  const currency = offer.currentCurrency ?? offer.priceHistoryCurrency;
  if (!amount || !currency) return null;

  return (
    <p className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <Price
        amount={amount}
        currency={currency}
        className="text-3xl font-bold leading-none tracking-tight text-muted-foreground line-through decoration-2"
      />
      <span className="text-[13px] text-muted-foreground">
        {t("lastAvailablePrice")} · {offer.providerDisplayName}
      </span>
    </p>
  );
}

function OutOfStockActions({
  historyExpanded,
  historyRegionId,
  onToggleHistory,
}: {
  historyExpanded: boolean;
  historyRegionId: string;
  onToggleHistory: () => void;
}) {
  const t = useTranslations("shared.affiliates");
  const onPriceAlert = useDealOfferPriceAlert();

  return (
    <div className="flex flex-wrap gap-2">
      {onPriceAlert && (
        <Button className="min-h-11 flex-[1_1_12rem] font-bold sm:flex-none" onClick={onPriceAlert}>
          <NotificationIcon aria-hidden="true" />
          {t("priceAlertCta")}
        </Button>
      )}
      <DealOfferHistoryButton
        variant="outline"
        mode="history"
        expanded={historyExpanded}
        controls={historyExpanded ? historyRegionId : undefined}
        onClick={onToggleHistory}
        className="flex-[1_1_9rem] sm:flex-none lg:min-h-11"
      />
    </div>
  );
}

export function OutOfStockDealOffer({
  currency,
  formatSwitch,
  offer,
  presentationScope,
}: {
  currency: string;
  /** Work scope: the segmented offer-format switch rendered under the panel heading. */
  formatSwitch?: ReactNode;
  offer: DealOffer;
  presentationScope: "release" | "work";
}) {
  const t = useTranslations("shared.affiliates");
  const tFormats = useTranslations("shared.releaseFilters");
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const historyRegionId = useId();
  const monitoringState = offer.monitoringFresh ? "active" : "stale";
  if (!offer.priceInsightsVisible) return null;

  return (
    <>
      <DealOfferPanel
        compact
        heading={t("outOfStockOfferHeading", {
          entityType: presentationScope,
          format: offerFormatLabel(offer, tFormats),
        })}
        headingTone="muted"
        headingMeta={<DealOfferCheckedAt lastCheckedAt={offer.lastCheckedAt} />}
      >
        {formatSwitch}
        {presentationScope === "work" && <BestDealOfferEditionRow offer={offer} />}
        <LastAvailablePrice offer={offer} />
        <p className="text-sm text-foreground/85">
          {t("outOfStockMonitoringMessage", { state: monitoringState })}
        </p>
        <OutOfStockActions
          historyExpanded={historyExpanded}
          historyRegionId={historyRegionId}
          onToggleHistory={() => setHistoryExpanded((current) => !current)}
        />
        {historyExpanded && (
          <div id={historyRegionId} className="border-t border-border/60 pt-3">
            <OfferPriceHistory offer={offer} currency={currency} emptyState="monitoring_started" />
          </div>
        )}
      </DealOfferPanel>
    </>
  );
}
